import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Paths } from '../paths';
import { useAuth } from 'services/auth/hooks';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { Loading } from 'components/Loading';
import { withPublicNavbar } from 'modules/layout/components/PublicNavBar';
import {
  POST_SSO_REDIRECT_KEY,
  resolvePostLoginRedirect
} from 'services/auth/utils/ssoRedirect';

type Props = {
  element: ReactElement;
};

export const PublicRoute: React.FC<Props> = ({ element }) => {
  const { userAuth, isLoading } = useAuth();
  const user = useRecoilValue(userAtom);
  const location = useLocation();

  const isOnboard = user?.isOnboard;

  if (isLoading) return <Loading />;

  // Avoid flashing the login form while the session check is pending.
  if (userAuth === undefined) return <Loading />;

  // An ?sso= deep-link has to re-authenticate against the named provider, so it
  // renders the login page to run the sign-out chain rather than being served
  // from the session already in the browser.
  if (new URLSearchParams(location.search).get('sso')) {
    return withPublicNavbar(element);
  }

  if (userAuth && isOnboard === false) {
    return <Navigate to={Paths.ONBOARDING} replace />;
  }

  if (userAuth) {
    // Prefer an explicit ?next= (deep-link while already signed in), else a
    // destination carried through SSO, ahead of the default redirect to Home.
    const ssoTarget = resolvePostLoginRedirect(
      new URLSearchParams(location.search).get('next'),
      sessionStorage.getItem(POST_SSO_REDIRECT_KEY)
    );
    if (ssoTarget) {
      sessionStorage.removeItem(POST_SSO_REDIRECT_KEY);
      return <Navigate to={ssoTarget} replace />;
    }

    const to = location.state?.from?.pathname
      ? location.state.from.pathname
      : Paths.HOME;

    // Redirect declaratively; navigation during render may be dropped.
    return (
      <Navigate
        to={{ pathname: to, search: location.state?.from?.search }}
        replace
      />
    );
  }

  return withPublicNavbar(element);
};
